import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTasks } from '../../hooks/useTasks';
import { 
  Plus, CheckCircle, Clock, AlertCircle, ChevronRight,
  Calendar, Flag, Filter, X, Trash2
} from 'lucide-react';
import TaskCard from './TaskCard';
import AddTaskModal from './AddTaskModal';

const MobileToDo = () => {
  const {
    tasks,
    loading,
    stats,
    createTask,
    updateTask,
    deleteTask,
    completeTask,
    refreshAll
  } = useTasks();

  const [activeTab, setActiveTab] = useState('active');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterBy, setFilterBy] = useState('manual');

  const visibleTasks = useMemo(
    () =>
      tasks.filter((task) =>
        activeTab === 'completed' ? task.status === 'completed' : task.status !== 'completed'
      ),
    [tasks, activeTab]
  );

  const totalActiveCount = useMemo(
    () => tasks.filter(task => task.status !== 'completed').length,
    [tasks]
  );
  const totalCompletedCount = useMemo(
    () => tasks.filter(task => task.status === 'completed').length,
    [tasks]
  );

  const manualVisibleCount = useMemo(
    () => visibleTasks.filter(task => task.source !== 'auto_generated').length,
    [visibleTasks]
  );
  const smartVisibleCount = useMemo(
    () => visibleTasks.filter(task => task.source === 'auto_generated').length,
    [visibleTasks]
  );

  const displayActiveCount = stats?.pending ?? totalActiveCount;
  const displayCompletedCount = stats?.completed ?? totalCompletedCount;

  // Filter tasks
  const filteredTasks = visibleTasks.filter(task => {
    if (filterBy === 'all') return true;
    if (filterBy === 'smart') return task.source === 'auto_generated';
    if (filterBy === 'manual') return task.source !== 'auto_generated';
    return true;
  });

  const sortedTasks = useMemo(() => {
    const base = [...filteredTasks];
    if (filterBy === 'all') {
      base.sort((a, b) => {
        const aIsManual = a.source !== 'auto_generated';
        const bIsManual = b.source !== 'auto_generated';
        if (aIsManual === bIsManual) return 0;
        return aIsManual ? -1 : 1;
      });
    }
    return base;
  }, [filteredTasks, filterBy]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with Tabs */}
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'active'
                ? 'text-[#1F11ED] border-b-2 border-[#1F11ED]'
                : 'text-gray-500'
            }`}
          >
            Active ({displayActiveCount})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'completed'
                ? 'text-[#1F11ED] border-b-2 border-[#1F11ED]'
                : 'text-gray-500'
            }`}
          >
            Completed ({displayCompletedCount})
          </button>
        </div>

        {/* Actions Bar */}
        <div className="p-4 flex items-center space-x-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-[#1F11ED] text-white rounded-lg hover:bg-[#1709C7]"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">Add Task</span>
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border transition-colors ${
              showFilters || filterBy !== 'all'
                ? 'bg-[#1F11ED] text-white border-[#1F11ED]'
                : 'bg-white text-gray-600 border-gray-300'
            }`}
          >
            <Filter className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="px-4 pb-4 space-y-3"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Task Type</label>
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
              >
                <option value="all">All Tasks</option>
                <option value="smart">Smart Tasks</option>
                <option value="manual">Manual Tasks</option>
              </select>
            </div>
            <button
              onClick={() => {
                setFilterBy('manual');
                setShowFilters(false);
              }}
              className="w-full text-sm text-gray-600 py-2"
            >
              Clear Filters
            </button>
          </motion.div>
        )}
      </div>

      {/* Summary Chips */}
      <div className="px-4 pt-3 pb-1 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
          <p className="text-xs font-medium text-blue-700 uppercase">Smart Tasks</p>
          <p className="text-lg font-semibold text-blue-900">{smartVisibleCount}</p>
          <p className="text-xs text-blue-600 mt-1">
            Automatically generated from your invoices and payments.
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
          <p className="text-xs font-medium text-gray-600 uppercase">Manual Tasks</p>
          <p className="text-lg font-semibold text-gray-900">{manualVisibleCount}</p>
          <p className="text-xs text-gray-500 mt-1">
            Tasks you created to stay on top of your workflow.
          </p>
        </div>
      </div>

      {/* Task List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : sortedTasks.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {activeTab === 'completed' ? 'No completed tasks' : 'No active tasks'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {activeTab === 'active' ? 'Add your first task to get started!' : 'Complete some tasks to see them here'}
            </p>
            {activeTab === 'active' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center space-x-2 px-6 py-2 bg-[#1F11ED] text-white rounded-lg hover:bg-[#1709C7]"
              >
                <Plus className="w-4 h-4" />
                <span>Add Task</span>
              </button>
            )}
          </div>
        ) : (
          sortedTasks.map((task, index) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
            >
              <div className="flex items-start space-x-3">
                <button
                  onClick={() => completeTask(task.id)}
                  className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    task.status === 'completed'
                      ? 'bg-green-500 border-green-500'
                      : 'border-gray-300 hover:border-[#1F11ED]'
                  }`}
                >
                  {task.status === 'completed' && (
                    <CheckCircle className="w-4 h-4 text-white" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <h3 className={`font-medium mb-1 ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                    {task.title}
                  </h3>
                  {task.description && (
                    <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                  )}
                  
                  <div className="flex items-center flex-wrap gap-2 text-xs">
                    {task.priority && (
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full ${getPriorityColor(task.priority)}`}>
                        <Flag className="w-3 h-3" />
                        <span className="capitalize">{task.priority}</span>
                      </span>
                    )}
                    {task.due_date && (
                      <span className="inline-flex items-center space-x-1 text-gray-600">
                        <Calendar className="w-3 h-3" />
                        <span>{new Date(task.due_date).toLocaleDateString('en-GB')}</span>
                      </span>
                    )}
                    {task.source === 'auto_generated' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                        <span>Smart Task</span>
                      </span>
                    )}
                  </div>
                </div>

                {task.status !== 'completed' && (
                  <button
                    onClick={() => deleteTask(task.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Add Task Modal */}
      {showAddModal && (
        <AddTaskModal
          onClose={() => setShowAddModal(false)}
          onCreate={createTask}
        />
      )}
    </div>
  );
};

export default MobileToDo;

